/**
 * UserManagementPage — Eva's surface for everything plan 01-05's API ships.
 *
 * Role-aware rendering (defense-in-depth — the backend already 403s on writes
 * for everyone except Content Admin, but the UI also hides the affordances):
 *   - content_admin: Create button + per-row Edit + Reset password
 *   - super_admin:   read-only banner; no write affordances rendered
 *   - hr_manager:    read-only (no banner; HR Manager edits assignments in Phase 2)
 *   - hr_recruiter / hiring_manager: 403 page (handled by RequireRole or by
 *     the backend's 403 surfaced as an in-page error)
 *
 * BUG-021: table chrome uses TOKENS.surfaceElevated + TOKENS.shadow + warm CTA.
 * BUG-022: HTML constraints + zod constraints paired in the create/edit dialogs.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { formatValidationError } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { canMutate } from "@/lib/permissions";
import { TOKENS } from "@/lib/tokens";
import { ApiError } from "@/types/api";
import type { UserOut } from "@/types/api";

import { UserCreateDialog } from "./UserCreateDialog";
import { UserEditDialog } from "./UserEditDialog";
import { useUsers } from "./useUsers";

const HR_BLOCKED = new Set(["hr_recruiter", "hiring_manager"]);

export function UserManagementPage() {
  const { t } = useTranslation(["users", "errors", "common"]);
  const currentUser = useAuthStore((s) => s.user);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<UserOut | null>(null);
  const [resetTarget, setResetTarget] = useState<UserOut | null>(null);

  const list = useUsers({});

  // Defense in depth: the backend 403s these roles too, but we also keep them
  // off the page so the surface matches their permissions.
  if (currentUser && HR_BLOCKED.has(currentUser.role)) {
    return (
      <div className="p-8 text-center" role="alert" style={{ color: TOKENS.text }}>
        <h1 className="text-2xl font-semibold mb-2">403</h1>
        <p style={{ color: TOKENS.textMuted }}>{t("errors:forbidden")}</p>
      </div>
    );
  }

  // Plan 02-10a Task 2 (Run3-A): migrate ad-hoc role check to canMutate.
  // Backend PERM_USER_CREATE held by content_admin only (USR-04: super_admin
  // deliberately cannot create users — privilege-escalation prevention).
  const canEdit = canMutate("user", "create", currentUser?.role);
  const isReadonly = currentUser?.role === "super_admin";

  return (
    <section className="max-w-6xl mx-auto">
      <header className="flex flex-row items-center justify-between gap-3 mb-4">
        <h1
          className="text-xl sm:text-2xl font-semibold"
          style={{ color: TOKENS.text }}
        >
          {t("users:title")}
        </h1>
        {canEdit && (
          <Button
            type="button"
            onClick={() => setCreateOpen(true)}
            style={{
              backgroundColor: TOKENS.primary,
              color: TOKENS.primaryFg,
              minHeight: "44px",
              fontWeight: 600,
            }}
          >
            {t("users:actions.create")}
          </Button>
        )}
      </header>

      {isReadonly && (
        <div
          role="status"
          className="px-4 py-3 mb-4 rounded-md"
          style={{
            backgroundColor: TOKENS.surfaceElevated,
            border: `1px solid ${TOKENS.borderStrong}`,
            color: TOKENS.text,
          }}
        >
          {t("users:read_only_banner")}
        </div>
      )}

      <div
        className="rounded-md overflow-x-auto"
        style={{
          backgroundColor: TOKENS.surfaceElevated,
          border: `1px solid ${TOKENS.border}`,
          boxShadow: TOKENS.shadow,
        }}
      >
        {list.isPending && (
          <div className="p-6" style={{ color: TOKENS.textMuted }}>
            {t("common:actions.loading")}
          </div>
        )}

        {list.isError && (
          <div className="p-6" role="alert" style={{ color: "#dc2626" }}>
            {formatValidationError(list.error)}
            {list.error instanceof ApiError && list.error.status === 403 && (
              <p className="mt-2" style={{ color: TOKENS.textMuted }}>
                {t("errors:forbidden")}
              </p>
            )}
          </div>
        )}

        {list.data && list.data.items.length === 0 && (
          <div className="p-6" style={{ color: TOKENS.textMuted }}>
            {t("users:table.no_users")}
          </div>
        )}

        {list.data && list.data.items.length > 0 && (
          <table className="w-full min-w-[640px] text-sm">
            <thead style={{ backgroundColor: TOKENS.surface }}>
              <tr>
                <th
                  className="text-left p-3 sticky left-0 z-10"
                  style={{
                    color: TOKENS.text,
                    backgroundColor: TOKENS.surface,
                    boxShadow: "2px 0 4px -2px rgba(0,0,0,0.08)",
                  }}
                >
                  {t("users:table.username")}
                </th>
                <th className="text-left p-3" style={{ color: TOKENS.text }}>
                  {t("users:table.role")}
                </th>
                <th className="text-left p-3" style={{ color: TOKENS.text }}>
                  {t("users:table.preferred_lang")}
                </th>
                <th className="text-left p-3" style={{ color: TOKENS.text }}>
                  {t("users:table.must_rotate")}
                </th>
                <th className="text-left p-3" style={{ color: TOKENS.text }}>
                  {t("users:table.is_active")}
                </th>
                {canEdit && (
                  <th className="text-right p-3" style={{ color: TOKENS.text }}>
                    {t("users:table.actions")}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((u) => (
                <tr
                  key={u.id}
                  style={{ borderTop: `1px solid ${TOKENS.border}` }}
                >
                  <td
                    className="p-3 sticky left-0 z-10"
                    style={{
                      color: TOKENS.text,
                      backgroundColor: TOKENS.surfaceElevated,
                      boxShadow: "2px 0 4px -2px rgba(0,0,0,0.08)",
                    }}
                  >
                    {u.username}
                  </td>
                  <td className="p-3" style={{ color: TOKENS.text }}>
                    {t(`users:roles.${u.role}`)}
                  </td>
                  <td className="p-3" style={{ color: TOKENS.text }}>
                    {t(`users:langs.${u.preferred_lang}`)}
                  </td>
                  <td className="p-3" style={{ color: TOKENS.text }}>
                    {u.password_must_rotate
                      ? t("users:must_rotate.yes")
                      : t("users:must_rotate.no")}
                  </td>
                  <td className="p-3" style={{ color: TOKENS.text }}>
                    {u.is_active
                      ? t("users:must_rotate.yes")
                      : t("users:must_rotate.no")}
                  </td>
                  {canEdit && (
                    <td className="p-3 text-right">
                      <div className="flex flex-row justify-end gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setEditTarget(u)}
                          style={{ minHeight: "44px" }}
                        >
                          {t("users:actions.edit")}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setResetTarget(u)}
                          style={{ minHeight: "44px" }}
                        >
                          {t("users:actions.reset_password")}
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {list.data && (
        <p
          className="text-xs mt-3 text-right"
          style={{ color: TOKENS.textMuted }}
        >
          {t("users:table.total_count", { total: list.data.total })}
        </p>
      )}

      {canEdit && (
        <>
          <UserCreateDialog
            open={createOpen}
            onClose={() => setCreateOpen(false)}
          />
          <UserEditDialog
            open={editTarget !== null}
            onClose={() => setEditTarget(null)}
            mode="edit"
            user={editTarget}
          />
          <UserEditDialog
            open={resetTarget !== null}
            onClose={() => setResetTarget(null)}
            mode="reset"
            user={resetTarget}
          />
        </>
      )}
    </section>
  );
}
